import { Dropdown } from '@/components/Dropdown';
import { ArrowRight, ListItemIcon, ListItemText, MenuItem } from '@/components/Dropdown/MenuItems/components';
import CalendarMark from '@/images/CalendarMark.svg';
import ClockCircle from '@/images/ClockCircle.svg';
import SortByAlphabet from '@/images/SortByAlphabet.svg';
import SortVertical from '@/images/SortVertical.svg';
import { ORDER, SORT_BY } from '@/state/video-library/videoLibrary';
import styled from '@emotion/styled';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const SortByContainer = styled.div`
    margin-bottom: 4px;
`;

const SortByText = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.secondary[600]};
`;

const SortByTextContainer = styled.div`
    margin: 4px 12px 8px 12px;
`;

type SortOption = {
    key: string;
    label: string;
    sort_by: SORT_BY;
    order: ORDER;
    icon: string;
};

const sortOptions = [
    { key: 'last-modified', label: 'Last modified', sort_by: 'modified_at', order: 'descending', icon: ClockCircle },
    { key: 'newest', label: 'Newest created', sort_by: 'created_at', order: 'descending', icon: CalendarMark },
    { key: 'oldest', label: 'Oldest created', sort_by: 'created_at', order: 'ascending', icon: CalendarMark },
    { key: 'az', label: 'Alphabetical (A-Z)', sort_by: 'name', order: 'ascending', icon: SortByAlphabet },
    { key: 'za', label: 'Alphabetical (Z-A)', sort_by: 'name', order: 'descending', icon: SortByAlphabet },
] satisfies SortOption[];

export const SortBy = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [open, setOpen] = useState(false);

    const activeSort = useMemo(() => searchParams.get('sort_by'), [searchParams]);
    const activeOrder = useMemo(() => searchParams.get('order'), [searchParams]);

    const items = useMemo(
        () =>
            sortOptions.map(option => ({
                label: (
                    <MenuItem
                        selected={option.sort_by === activeSort && option.order === activeOrder}
                        onClick={() => {
                            setSearchParams(currentSearchParams => {
                                currentSearchParams.set('sort_by', option.sort_by);
                                currentSearchParams.set('order', option.order);
                                return currentSearchParams;
                            });
                            setOpen(false);
                        }}>
                        <ListItemIcon>
                            <img src={option.icon} />
                        </ListItemIcon>
                        <ListItemText>{option.label}</ListItemText>
                    </MenuItem>
                ),
            })),
        [activeSort, activeOrder, setSearchParams]
    );

    const activeOption = useMemo(
        () => sortOptions.find(option => option.sort_by === activeSort && option.order === activeOrder) || sortOptions[0],
        [activeSort, activeOrder]
    );

    const renderMenuWrapper = useCallback(
        (children: React.ReactNode) => (
            <SortByContainer>
                <SortByTextContainer>
                    <SortByText>Sort by</SortByText>
                </SortByTextContainer>
                {children}
            </SortByContainer>
        ),
        []
    );

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);

    return (
        <Dropdown
            closed={!open}
            onClose={handleClose}
            onOpen={handleOpen}
            trigger={
                <MenuItem>
                    <ListItemIcon>
                        <img width={20} height={20} src={SortVertical} />
                    </ListItemIcon>
                    <ListItemText>{activeOption.label}</ListItemText>
                    <ArrowRight>{!open ? <ExpandLess /> : <ExpandMore />}</ArrowRight>
                </MenuItem>
            }
            renderMenuWrapper={renderMenuWrapper}
            items={items}
        />
    );
};
